import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Download, ExternalLink, Save, Loader2, Search, Printer } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../hooks/use-toast';
import { hubUrl } from '../../pages/guest-hub/hubConfig';
import { QR_LOGO_DATA_URI } from '../../pages/guest-hub/HubBrand';
import { downloadPNG, downloadSVG } from '../../pages/PropertyQRCodes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GuidebookQRCard = ({ prop, size = 160 }) => {
  const id = `gb-qr-${prop.slug}`;
  const url = hubUrl(prop.slug, 'guidebook');
  return (
    <Card data-testid={`gb-qr-card-${prop.slug}`}>
      <CardHeader className="pb-3"><CardTitle className="text-base">Guidebook QR code</CardTitle></CardHeader>
      <CardContent className="flex flex-col items-center gap-3">
        <QRCodeSVG id={id} value={url} size={size} level="H" marginSize={2} bgColor="#ffffff" fgColor="#0f172a"
          imageSettings={{ src: QR_LOGO_DATA_URI, height: Math.round(size * 0.24), width: Math.round(size * 0.24), excavate: true }} />
        <a href={url} target="_blank" rel="noreferrer" data-testid={`gb-qr-link-${prop.slug}`} className="text-xs text-slate-500 break-all text-center hover:text-amber-600 inline-flex items-center gap-1">{url}<ExternalLink className="h-3 w-3" /></a>
        <div className="flex gap-2">
          <Button data-testid={`gb-qr-png-${prop.slug}`} size="sm" variant="outline" onClick={() => downloadPNG(id, `${prop.slug}-guidebook-qr`)}><Download className="h-4 w-4 mr-1" />PNG</Button>
          <Button data-testid={`gb-qr-svg-${prop.slug}`} size="sm" variant="outline" onClick={() => downloadSVG(id, `${prop.slug}-guidebook-qr`)}><Download className="h-4 w-4 mr-1" />SVG</Button>
        </div>
        <Link to={`/qr-signs?mode=guidebook&prop=${prop.slug}`} target="_blank" data-testid={`gb-qr-sign-${prop.slug}`} className="text-xs text-amber-600 hover:underline inline-flex items-center gap-1">
          <Printer className="h-3.5 w-3.5" />Printable "Scan for House Guidebook" sign
        </Link>
      </CardContent>
    </Card>
  );
};

export const GuidebookSeoCard = ({ prop }) => {
  const { token } = useAuth();
  const key = `guidebook-${prop.id}`;
  const [form, setForm] = useState({ meta_title: '', meta_description: '', no_index: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/public/seo/${key}`).then((r) => (r.ok ? r.json() : {})).then((d) => d?.meta_title !== undefined && setForm({ meta_title: d.meta_title || '', meta_description: d.meta_description || '', no_index: d.no_index ?? true })).catch(() => {});
  }, [key]);

  const save = async () => {
    setSaving(true);
    const res = await fetch(`${API_URL}/api/admin/seo/${key}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(form) });
    setSaving(false);
    toast({ title: res.ok ? 'Guidebook SEO saved' : 'SEO save failed', variant: res.ok ? undefined : 'destructive' });
  };

  return (
    <Card data-testid="gb-seo-card">
      <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Search className="h-4 w-4 text-amber-500" />Guidebook SEO</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1"><Label>Page title</Label><Input data-testid="gb-seo-title" value={form.meta_title} placeholder={`House Guidebook – ${prop.name} | Capitol View Stays`} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} /></div>
        <div className="space-y-1"><Label>Description</Label><Textarea data-testid="gb-seo-description" rows={3} value={form.meta_description} placeholder={`Check-in details, house rules and how-tos for your stay at ${prop.name}.`} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} /></div>
        <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="gb-seo-noindex" className="accent-amber-500" checked={form.no_index} onChange={(e) => setForm({ ...form, no_index: e.target.checked })} />Hide from Google (recommended – private guest info)</label>
        <Button data-testid="gb-seo-save" size="sm" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Save SEO</>}</Button>
      </CardContent>
    </Card>
  );
};
